import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from '@/hooks/use-toast';
import { Pencil, Trash2, MessageSquare, X, LogIn } from 'lucide-react';
import { MarkdownEditor } from '@/components/forum/MarkdownEditor';
import { Markdown } from '@/components/forum/Markdown';
import { useAuth } from '@/contexts/AuthContext';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  listComments, addComment, updateComment, deleteComment, isOwnedComment,
  type BlogComment,
} from '@/lib/blogComments';
import { useAutosaveDraft, loadDraft, clearDraft } from '@/lib/forumDrafts';

interface Props { postId: string; postSlug?: string }

interface Draft { name: string; body: string }

export function Comments({ postId, postSlug }: Props) {
  const { user, profile } = useAuth() as any;
  const [comments, setComments] = useState<BlogComment[]>([]);
  const [loading, setLoading] = useState(true);
  const draftKey = `ere_blog_comment_draft_v1:${postId}`;
  const initial = loadDraft<Draft>(draftKey);
  const [draft, setDraft] = useState<Draft>(initial || { name: '', body: '' });
  const [submitting, setSubmitting] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editBody, setEditBody] = useState('');
  const [deleteId, setDeleteId] = useState<string | null>(null);

  useAutosaveDraft(draftKey, draft, (d) => !d.body.trim() && !d.name.trim());

  const refresh = async () => {
    try {
      const c = await listComments(postId);
      setComments(c);
    } catch (e: any) {
      toast({ title: 'Failed to load comments', description: e.message, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
    const ch = supabase
      .channel(`blog_comments:${postId}`)
      .on('postgres_changes',
        { event: '*', schema: 'public', table: 'blog_comments', filter: `post_id=eq.${postId}` },
        () => refresh()
      )
      .subscribe();
    return () => { supabase.removeChannel(ch); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [postId]);

  const submit = async (parentId?: string) => {
    if (!user && !draft.name.trim()) {
      toast({ title: 'Name is required', variant: 'destructive' });
      return;
    }
    if (!draft.body.trim()) {
      toast({ title: 'Comment cannot be empty', variant: 'destructive' });
      return;
    }
    setSubmitting(true);
    try {
      await addComment({
        postId,
        authorName: user ? (profile?.full_name || user.email || 'User') : draft.name,
        body: draft.body,
        parentId,
      });
      clearDraft(draftKey);
      setDraft({ name: draft.name, body: '' });
      toast({ title: 'Comment posted' });
      refresh();
    } catch (e: any) {
      toast({ title: 'Could not post', description: e.message, variant: 'destructive' });
    } finally {
      setSubmitting(false);
    }
  };

  const saveEdit = async () => {
    if (!editingId || !editBody.trim()) return;
    try {
      await updateComment(editingId, editBody);
      toast({ title: 'Comment updated' });
      setEditingId(null);
      refresh();
    } catch (e: any) {
      toast({ title: 'Update failed', description: e.message, variant: 'destructive' });
    }
  };

  const confirmDelete = async () => {
    if (!deleteId) return;
    try {
      await deleteComment(deleteId);
      toast({ title: 'Comment deleted' });
      setDeleteId(null);
      refresh();
    } catch (e: any) {
      toast({ title: 'Delete failed', description: e.message, variant: 'destructive' });
    }
  };

  const fmt = (s: string) => new Date(s).toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' });
  const loginHref = `/login?redirect=${encodeURIComponent(`/blogs/${postSlug || postId}#comments`)}`;
  const myDisplay = profile?.full_name || user?.email || 'You';
  const myAvatar = profile?.avatar_url || user?.user_metadata?.avatar_url;

  return (
    <section className="mt-12 border-t pt-8" id="comments">
      <h2 className="font-display text-xl font-bold mb-5 flex items-center gap-2">
        <MessageSquare className="h-5 w-5" /> Comments ({comments.length})
      </h2>

      <div className="rounded-xl border bg-card p-4 mb-6 space-y-3">
        {user ? (
          <div className="flex items-center gap-2 text-sm">
            {myAvatar ? (
              <img src={myAvatar} alt={myDisplay} className="h-7 w-7 rounded-full object-cover" />
            ) : (
              <div className="h-7 w-7 rounded-full bg-primary/10 text-primary text-xs font-bold inline-flex items-center justify-center">
                {myDisplay.charAt(0).toUpperCase()}
              </div>
            )}
            <span className="text-muted-foreground">Posting as</span>
            <span className="font-semibold truncate">{myDisplay}</span>
          </div>
        ) : (
          <div className="flex items-center justify-between gap-3 flex-wrap rounded-lg bg-muted/40 px-3 py-2 text-xs">
            <span className="text-muted-foreground">Commenting as a guest. Your comment is tied to this device only.</span>
            <Link to={loginHref} className="inline-flex items-center gap-1 font-semibold text-primary hover:underline">
              <LogIn className="h-3 w-3" /> Sign in to comment as yourself
            </Link>
          </div>
        )}

        {!user && (
          <div>
            <Label className="text-xs">Your name</Label>
            <Input
              maxLength={80}
              value={draft.name}
              onChange={(e) => setDraft({ ...draft, name: e.target.value })}
              placeholder="Jane Doe"
            />
          </div>
        )}
        <div>
          <Label className="text-xs">Comment</Label>
          <MarkdownEditor
            value={draft.body}
            onChange={(v) => setDraft({ ...draft, body: v })}
            placeholder="Share your thoughts… markdown supported"
            maxLength={5000}
            minHeight={120}
          />
        </div>
        <div className="flex items-center justify-between">
          <span className="text-[11px] text-muted-foreground">{draft.body || draft.name ? 'Draft autosaved' : ''}</span>
          <div className="flex gap-2">
            {(draft.body || draft.name) && (
              <Button variant="ghost" size="sm" onClick={() => { clearDraft(draftKey); setDraft({ name: '', body: '' }); }}>
                Discard
              </Button>
            )}
            <Button size="sm" onClick={() => submit()} disabled={submitting}>
              {submitting ? 'Posting…' : 'Post comment'}
            </Button>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="text-center text-sm text-muted-foreground py-6">Loading comments…</div>
      ) : comments.length === 0 ? (
        <div className="text-center text-sm text-muted-foreground py-6">Be the first to comment.</div>
      ) : (
        <ul className="space-y-4">
          {comments.map((c) => {
            const owned = isOwnedComment(c, user?.id);
            const isEditing = editingId === c.id;
            return (
              <li key={c.id} className="rounded-xl border bg-card p-4">
                <div className="flex items-center justify-between gap-3 mb-2">
                  <div className="flex items-center gap-2 min-w-0">
                    {c.author_avatar_url ? (
                      <img src={c.author_avatar_url} alt={c.author_name} className="h-7 w-7 rounded-full object-cover shrink-0" />
                    ) : (
                      <div className="h-7 w-7 rounded-full bg-primary/10 text-primary text-xs font-bold inline-flex items-center justify-center shrink-0">
                        {c.author_name.charAt(0).toUpperCase()}
                      </div>
                    )}
                    <div className="text-sm font-semibold truncate">{c.author_name}</div>
                    {c.user_id && <span className="text-[10px] uppercase tracking-wide text-primary font-bold">verified</span>}
                    <div className="text-[11px] text-muted-foreground">{fmt(c.created_at)}{c.updated_at !== c.created_at && ' · edited'}</div>
                  </div>
                  {owned && !isEditing && (
                    <div className="flex items-center gap-1 shrink-0">
                      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => { setEditingId(c.id); setEditBody(c.body); }}>
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setDeleteId(c.id)}>
                        <Trash2 className="h-3.5 w-3.5 text-destructive" />
                      </Button>
                    </div>
                  )}
                </div>
                {isEditing ? (
                  <div className="space-y-2">
                    <MarkdownEditor value={editBody} onChange={setEditBody} maxLength={5000} minHeight={100} />
                    <div className="flex justify-end gap-2">
                      <Button size="sm" variant="ghost" onClick={() => setEditingId(null)}><X className="h-4 w-4 mr-1" /> Cancel</Button>
                      <Button size="sm" onClick={saveEdit}>Save</Button>
                    </div>
                  </div>
                ) : (
                  <Markdown>{c.body}</Markdown>
                )}
              </li>
            );
          })}
        </ul>
      )}

      <AlertDialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this comment?</AlertDialogTitle>
            <AlertDialogDescription>This action cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
}
